import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { KeyRound, Plus, Eye, EyeOff, Copy, Trash2, Pencil, AlertTriangle, Loader2 } from 'lucide-react'
import { getSession } from '../lib/session'

// The tenant's own Secrets & Variables. Referenced as ${NAME} by agent tools,
// connectors, workflows, and adapters. Encrypted server-side; values never leave
// the server unless the user opts a variable into "expose to the AI".
export default function UserVariables() {
  const ns = getSession()?.namespaceId
  const [vars, setVars] = useState([])
  const [loading, setLoading] = useState(true)
  const [revealed, setRevealed] = useState({}) // name -> value
  const [editing, setEditing] = useState(null) // name being edited (value blank = keep)
  const blank = { name: '', value: '', isSecret: true, exposeToLlm: false, description: '' }
  const [form, setForm] = useState(blank)
  const [busy, setBusy] = useState(false)

  const load = () => {
    if (!ns) { setLoading(false); return }
    axios.get(`/api/v1/tenants/${ns}/variables`).then(r => setVars(r.data.variables || [])).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(load, [ns])

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return }
    setBusy(true)
    try {
      await axios.post(`/api/v1/tenants/${ns}/variables`, {
        name: form.name.trim(), value: form.value, isSecret: form.isSecret, exposeToLlm: form.exposeToLlm, description: form.description || undefined,
      })
      toast.success(editing ? 'Updated' : 'Added')
      setForm(blank); setEditing(null); load()
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed') } finally { setBusy(false) }
  }

  const reveal = async (name) => {
    if (revealed[name] != null) { setRevealed(r => { const n = { ...r }; delete n[name]; return n }); return }
    try { const { data } = await axios.post(`/api/v1/tenants/${ns}/variables/${name}/reveal`); setRevealed(r => ({ ...r, [name]: data.value })) }
    catch { toast.error('Could not reveal') }
  }
  const copy = async (name) => {
    try {
      const val = revealed[name] ?? (await axios.post(`/api/v1/tenants/${ns}/variables/${name}/reveal`)).data.value
      await navigator.clipboard.writeText(val); toast.success('Copied')
    } catch { toast.error('Copy failed') }
  }
  const edit = (v) => { setEditing(v.name); setForm({ name: v.name, value: '', isSecret: v.isSecret, exposeToLlm: v.exposeToLlm, description: v.description || '' }); }
  const remove = async (name) => {
    if (!confirm(`Delete variable ${name}?`)) return
    try { await axios.delete(`/api/v1/tenants/${ns}/variables/${name}`); toast.success('Deleted'); load() } catch { toast.error('Delete failed') }
  }

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound size={20} className="text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-900">Secrets &amp; Variables</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">Your own API keys and config values. Reference them anywhere our services run — agent tools, connectors, workflows, adapters — as <code className="bg-gray-100 px-1 rounded">${'{NAME}'}</code>. Encrypted at rest; used server-side and never sent to the AI unless you turn on “expose” below.</p>

      {/* Add / edit form */}
      <div className="rounded-xl border border-gray-200 p-4 space-y-3 bg-gray-50/60">
        <div className="grid sm:grid-cols-2 gap-3">
          <input className="input" placeholder="NAME (e.g. STRIPE_KEY)" value={form.name} disabled={!!editing}
            onChange={e => setForm(f => ({ ...f, name: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') }))} />
          <input className="input" type="password" placeholder={editing ? 'New value (blank = keep current)' : 'Value'} value={form.value}
            onChange={e => setForm(f => ({ ...f, value: e.target.value }))} />
        </div>
        <input className="input" placeholder="Description (optional)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.isSecret} onChange={e => setForm(f => ({ ...f, isSecret: e.target.checked }))} /> Secret (mask in list)</label>
          <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.exposeToLlm} onChange={e => setForm(f => ({ ...f, exposeToLlm: e.target.checked }))} /> Expose this value to the AI</label>
        </div>
        {form.exposeToLlm && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>When on, this value is included in prompts sent to the AI model (and its provider), which may log it. Never enable it for passwords, private keys, or payment credentials — leave those off and reference them as <code className="bg-amber-500/10 px-1 rounded">${'{NAME}'}</code>; the server substitutes them without the model ever seeing the value.</span>
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={save} disabled={busy} className="btn-primary flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}{editing ? 'Update' : 'Add variable'}
          </button>
          {editing && <button onClick={() => { setEditing(null); setForm(blank) }} className="btn-secondary">Cancel</button>}
        </div>
      </div>

      {/* List */}
      <div className="mt-4">
        {loading ? <p className="text-sm text-gray-400">Loading…</p>
          : vars.length === 0 ? <p className="text-sm text-gray-400">No variables yet. Add your first above.</p>
            : (
              <div className="divide-y divide-gray-100">
                {vars.map(v => (
                  <div key={v.name} className="flex items-center gap-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-mono font-medium text-gray-900 flex items-center gap-2">
                        {v.name}
                        {v.exposeToLlm && <span className="text-[9px] uppercase tracking-wide bg-amber-500/15 text-amber-700 px-1 py-0.5 rounded">AI-visible</span>}
                      </p>
                      <p className="text-xs text-gray-500 font-mono truncate">{revealed[v.name] != null ? revealed[v.name] : (v.preview || (v.isSecret ? '••••••' : ''))}{v.description ? <span className="font-sans text-gray-400"> — {v.description}</span> : ''}</p>
                    </div>
                    <button onClick={() => reveal(v.name)} title="Reveal" className="p-1.5 text-gray-400 hover:text-gray-800">{revealed[v.name] != null ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                    <button onClick={() => copy(v.name)} title="Copy" className="p-1.5 text-gray-400 hover:text-gray-800"><Copy size={15} /></button>
                    <button onClick={() => edit(v)} title="Edit" className="p-1.5 text-gray-400 hover:text-gray-800"><Pencil size={15} /></button>
                    <button onClick={() => remove(v.name)} title="Delete" className="p-1.5 text-gray-400 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            )}
      </div>
    </div>
  )
}
